import { ListSkeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import type { WorkflowDiagnosticsPage } from '@/domain/workflow-admin.types'
import { DIAGNOSTIC_CODE_LABEL } from '@/domain/workflow-admin.types'
import { ErrorState } from './states'

/**
 * The workflow-configuration diagnostics report. Every row is rendered from the
 * machine-readable `code`; the server's `message` is shown as supporting detail
 * and is never parsed to decide what a finding is.
 */

export interface DiagnosticsTableProps {
	report?: WorkflowDiagnosticsPage
	isLoading?: boolean
	error?: unknown
	onRetry?: () => void
	onPageChange?: (page: number) => void
}

/** The tenant-safe identifier a finding carries, in the order most useful to read. */
function contextText(context: WorkflowDiagnosticsPage['data'][number]['context']): string {
	const parts = [
		context.orderCode && `order ${context.orderCode}`,
		context.workflowCode && `workflow ${context.workflowCode}`,
		context.statusCode && `status ${context.statusCode}`,
		context.ruleId && `rule ${context.ruleId}`,
	].filter(Boolean)
	return parts.length > 0 ? parts.join(' · ') : '—'
}

export function DiagnosticsTable({ report, isLoading, error, onRetry, onPageChange }: DiagnosticsTableProps) {
	if (isLoading) return <ListSkeleton rows={5} label="Loading diagnostics" />
	if (error) return <ErrorState error={error} onRetry={onRetry} title="Could not load diagnostics" />
	if (!report) return null

	if (report.data.length === 0) {
		return (
			<div
				role="status"
				className="rounded-ds-base border border-ds-success/30 bg-ds-success/10 p-6 text-center text-sm"
			>
				<p className="font-medium text-ds-success">Workflow configuration is healthy</p>
				<p className="text-ds-success">Diagnostics found no problems for this tenant.</p>
			</div>
		)
	}

	const { page, totalPages, total } = report.pagination
	return (
		<div className="space-y-3">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Finding</TableHead>
						<TableHead>Subject</TableHead>
						<TableHead>Resource</TableHead>
						<TableHead>Context</TableHead>
						<TableHead>Detail</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{report.data.map((finding) => (
						<TableRow key={`${finding.code}:${finding.resourceId ?? 'tenant'}`}>
							<TableCell>
								<Badge variant="danger" size="sm">
									{DIAGNOSTIC_CODE_LABEL[finding.code]}
								</Badge>
							</TableCell>
							<TableCell>{finding.subject}</TableCell>
							<TableCell className="text-ds-text-muted">{finding.resourceId ?? '—'}</TableCell>
							<TableCell>{contextText(finding.context)}</TableCell>
							<TableCell className="text-sm text-ds-text-muted">{finding.message}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			{totalPages > 1 && (
				<div className="flex items-center gap-3 text-sm">
					<Button variant="outline" isDisabled={page <= 1} onPress={() => onPageChange?.(page - 1)}>
						Previous
					</Button>
					<span>
						Page {page} of {totalPages} ({total} findings)
					</span>
					<Button variant="outline" isDisabled={page >= totalPages} onPress={() => onPageChange?.(page + 1)}>
						Next
					</Button>
				</div>
			)}
		</div>
	)
}
